import { useRef, useState } from "react";
import { Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, errMsg } from "@/lib/api";
import { ASPECTS, ratioOf } from "@/lib/aspects";

const boxStyle = (ratio) => {
  const [w, h] = ratio.split("/").map(Number);
  return w / h <= 1 ? { aspectRatio: ratio, height: 240, maxWidth: "100%" } : { aspectRatio: ratio, width: "100%" };
};

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd);
  return data.url;
};

export const ImageField = ({ label, value, onChange, aspect = "aspect-video", testid, hint, aspectKey, onAspect, aspectFallback = "landscape" }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const ratio = onAspect ? ratioOf(aspectKey, aspectFallback) : null;
  const original = onAspect && !ratio;

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file)); toast.success("Image uploaded"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div data-testid={testid}>
      {label && <p className="admin-label">{label}</p>}
      <div className={onAspect ? "flex justify-center" : ""}>
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files?.[0]); }}
        style={ratio ? boxStyle(ratio) : undefined}
        className={`${onAspect ? (original ? "w-full aspect-[4/3]" : "") : aspect} relative cursor-pointer border-2 border-dashed border-[#c9a227]/70 bg-[#fbf7ec]/70 hover:bg-[#c9a227]/10 transition-colors grid place-items-center overflow-hidden`}
      >
        {value ? <img src={imgUrl(value)} alt="" className={`absolute inset-0 w-full h-full ${original ? "object-contain" : "object-cover"}`} /> : (
          <span className="text-center text-sm text-[#5e4a35] p-3"><ImageIcon className="w-6 h-6 mx-auto mb-1" />{busy ? "Uploading…" : "Click or drop an image"}</span>
        )}
        {busy && value && <span className="absolute inset-0 bg-black/40 grid place-items-center text-white text-sm">Uploading…</span>}
      </div>
      </div>
      {onAspect && (
        <select value={aspectKey || aspectFallback} onChange={(e) => onAspect(e.target.value)} data-testid={`${testid}-aspect`} className="admin-input mt-2 text-xs">
          {ASPECTS.map((a) => <option key={a.key} value={a.key}>Box shape: {a.label}</option>)}
        </select>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testid}-file`} onChange={(e) => handle(e.target.files?.[0])} />
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => ref.current?.click()} data-testid={`${testid}-upload`} className="inline-flex items-center gap-1 text-xs font-heading border border-[#c9a227] px-2 py-1 hover:bg-[#c9a227]/20"><Upload className="w-3 h-3" /> Upload</button>
        {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} className="inline-flex items-center gap-1 text-xs font-heading border border-[#991b1b]/50 text-[#991b1b] px-2 py-1 hover:bg-[#991b1b]/10"><X className="w-3 h-3" /> Remove</button>}
      </div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" data-testid={`${testid}-url`} className="admin-input mt-2 text-xs" />
      {hint && <p className="text-xs italic mt-1 text-[#5e4a35]">{hint}</p>}
    </div>
  );
};
